import { FC } from 'react';
import { useQuery } from 'react-query';
import useToast from '@/hooks/use-toast.hook';
import useFormHandle from '@/hooks/use-form-handle.hook';
import FormSection from '@/components/form/form-section';
import InputControl from '@/components/form/input-control';
import FormButtons from '@/components/form/form-buttons';
import useQueryHelper from '@/hooks/use-query-helper';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { AgentModel } from '@/lib/models/agent.model';
import { getAgentById, updateAgent } from '@/lib/services/agent.service';

const fields = ['dataSourceIds'];
const formModel = new AgentModel();

interface Props {
  agentId: string;
}

const AgentDatasourcesForm: FC<Props> = ({ agentId }) => {
  const { errorToast, toast } = useToast();
  const { canChangeAgents } = useFeature();

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      const updated = await updateAgent(values);
      setSubmitting(false);

      if (updated) {
        resetForm({ values });
        toast('The assistant was updated');
        return;
      }

      errorToast('The assistant was not updated');
    },
  });

  const agentQuery = useQuery([QueryKey.AgentView], async () => getAgentById(agentId));
  const { showLoader: agentLoader } = useQueryHelper(agentQuery);

  if (agentLoader) {
    return;
  }

  return (
    <Form formHandle={formHandle} query={agentQuery}>
      <PageFeatureToast featureId="change-agent" can={canChangeAgents} />
      <FormSection title="Data Source Assignment" scrollId="data-source-assignment">
        <InputControl
          name="dataSourceIds"
          label="Context"
          formHandle={formHandle}
          readonly={!canChangeAgents}
          type="datasources-list-toggle"
          leftTitle="Available Data Sources"
          rightTitle="Assigned Data Sources"
        />
      </FormSection>

      <FormButtons formHandle={formHandle} readonly={!canChangeAgents} />
    </Form>
  );
};

export default AgentDatasourcesForm;
